import type { DisplayCurrency } from '@platform/types';
import { SUPPORTED_DISPLAY_CURRENCIES } from '@platform/types';

/**
 * Money formatting + display-currency detection for the multi-region UI.
 *
 * Plans are billed in their own currency (USD). Everything in this module is
 * presentation-only: we show an approximate converted amount next to the real
 * billed price, never instead of it.
 */

/** Format integer cents in any ISO 4217 currency for the user's locale. */
export function formatMoney(cents: number, currency: string, locale?: string): string {
  return new Intl.NumberFormat(locale ?? navigator.language, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/** Region (ISO 3166-1 alpha-2) → display currency. Only regions whose
 *  currency we can actually convert to are listed; everyone else sees USD. */
const REGION_TO_CURRENCY: Record<string, DisplayCurrency> = {
  US: 'usd',
  GB: 'gbp',
  AU: 'aud',
  NZ: 'nzd',
  CA: 'cad',
  // Eurozone
  AT: 'eur', BE: 'eur', DE: 'eur', ES: 'eur', FI: 'eur', FR: 'eur',
  GR: 'eur', IE: 'eur', IT: 'eur', NL: 'eur', PT: 'eur',
};

export function isDisplayCurrency(value: string): value is DisplayCurrency {
  return (SUPPORTED_DISPLAY_CURRENCIES as readonly string[]).includes(value);
}

/** Best-effort display currency from the browser locale. Falls back to USD. */
export function detectDisplayCurrency(): DisplayCurrency {
  try {
    const region = new Intl.Locale(navigator.language).maximize().region;
    if (region && REGION_TO_CURRENCY[region]) return REGION_TO_CURRENCY[region];
  } catch {
    // Malformed locale string — fall through to USD.
  }
  return 'usd';
}

/** Default Twilio number-search country for a display currency. Only the
 *  four countries TwilioService supports are mapped; everything else
 *  (including CAD/EUR) defaults to US. */
const CURRENCY_TO_COUNTRY: Partial<Record<DisplayCurrency, string>> = {
  usd: 'US',
  gbp: 'GB',
  aud: 'AU',
  nzd: 'NZ',
};

export function defaultNumberCountry(displayCurrency: string | undefined): string {
  if (displayCurrency && isDisplayCurrency(displayCurrency)) {
    return CURRENCY_TO_COUNTRY[displayCurrency] ?? 'US';
  }
  return 'US';
}

/** Options for the org-settings currency selector. */
export const DISPLAY_CURRENCY_OPTIONS: Array<{ value: DisplayCurrency; label: string }> = [
  { value: 'usd', label: 'US Dollar (USD)' },
  { value: 'gbp', label: 'British Pound (GBP)' },
  { value: 'aud', label: 'Australian Dollar (AUD)' },
  { value: 'nzd', label: 'New Zealand Dollar (NZD)' },
  { value: 'cad', label: 'Canadian Dollar (CAD)' },
  { value: 'eur', label: 'Euro (EUR)' },
];
